import React, { useState } from "react";

const BinaryToDecimalStep = () => {
  const [binary, setBinary] = useState("");
  const [bits, setBits] = useState([]);
  const [stepIndex, setStepIndex] = useState(0);
  const [calculatedValues, setCalculatedValues] = useState([]);
  const [valueInput, setValueInput] = useState("");
  const [feedback, setFeedback] = useState("");
  const [finalAnswer, setFinalAnswer] = useState("");
  const [finalFeedback, setFinalFeedback] = useState("");
  const [showActivity, setShowActivity] = useState(false);
  const [showFinal, setShowFinal] = useState(false);
  const [showSolution, setShowSolution] = useState(false);

  const handleStart = () => {
    if (!/^[01]+$/.test(binary.trim())) {
      alert("Please enter a valid binary number (only 0 and 1).");
      return;
    }
    setBits(binary.trim().split("").reverse());
    setStepIndex(0);
    setCalculatedValues([]);
    setFeedback("");
    setShowActivity(true);
    setShowFinal(false);
    setShowSolution(false);
  };

  const handleReset = () => {
    setBinary("");
    setBits([]);
    setStepIndex(0);
    setCalculatedValues([]);
    setFeedback("");
    setFinalAnswer("");
    setFinalFeedback("");
    setShowActivity(false);
    setShowFinal(false);
    setShowSolution(false);
  };

  const handleSubmitStep = () => {
    if (valueInput === "") {
      alert("Enter a value first.");
      return;
    }
    const bit = Number(bits[stepIndex]);
    const pos = stepIndex;
    const correct = bit * Math.pow(2, pos);

    if (Number(valueInput) === correct) {
      setCalculatedValues((prev) => [...prev, correct]);
      setStepIndex((prev) => prev + 1);
      setFeedback("");
      setValueInput("");
      if (stepIndex + 1 >= bits.length) {
        setShowFinal(true);
      }
    } else {
      setFeedback(`❌ Oops! Hint: ${bit} × 2^${pos} = ${correct}. `);
    }
  };

  const handleCheckFinal = () => {
    if (finalAnswer.trim() === "") {
      setFinalFeedback("Please enter a number.");
      return;
    }
    const correctSum = calculatedValues.reduce((a, b) => a + b, 0);
    if (Number(finalAnswer) === correctSum) {
      setFinalFeedback(`✅ Correct! Well done! `);
      setShowSolution(true);
    } else {
      setFinalFeedback("❌ Not quite. Try summing again carefully. ");
      setShowSolution(false);
    }
  };

  const handleShowSolution = () => {
    setShowSolution(true);
    setFinalFeedback(" Solution revealed below!");
  };

  const correctSum = calculatedValues.reduce((a, b) => a + b, 0);

  const styles = {
    body: {
      minHeight: "100vh",
      background: "linear-gradient(135deg, #ff9a9e, #fad0c4, #fbc2eb, #a6c1ee)",
      display: "flex",
      justifyContent: "center",
      alignItems: "flex-start",
      paddingTop: "30px",
      fontFamily: "'Comic Neue', cursive",
    },
    card: {
      maxWidth: "700px",
      background: "linear-gradient(145deg, #fff9f0, #fffcf5)",
      borderRadius: "20px",
      boxShadow: "0 15px 40px rgba(0,0,0,0.15)",
      padding: "28px",
      margin: "28px auto",
      color: "#333",
    //   border: "2px inset rgb(0, 0, 0)"
    },
    title: {
      textAlign: "center",
      fontSize: "26px",
      fontWeight: "700",
      background: "linear-gradient(to right, #ff6ec4, #7873f5, #4ade80)",
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
      marginBottom: "6px",
    },
    meta: { textAlign: "center", fontSize: "15px", marginBottom: "20px" },
    row: {
      display: "flex",
      gap: "12px",
      justifyContent: "center",
      flexWrap: "wrap",
      marginBottom: "16px",
    },
    label: { fontWeight: 600, color: "#ff4c4c" },
    input: {
      padding: "10px 14px",
      margin: "0 auto", 

      borderRadius: "12px",
      border: "2px solid #00f7ff",
      fontSize: "16px",
      textAlign: "center",
      width: "140px",
      background: "#fff0f5",
      color: "#333",
    },
    button: {
      background: "linear-gradient(135deg, #ff9a9e, #fad0c4)",
      color: "#333",
      border: 0,
      padding: "10px 18px",
      borderRadius: "12px",
      cursor: "pointer",
      fontWeight: "700",
      transition: "0.3s",
    },
    secondary: {
      background: "linear-gradient(135deg, #fbc2eb, #a6c1ee)",
      color: "#333",
      border: "none",
      borderRadius: "12px",
      padding: "10px 18px",
      fontWeight: "700",
      cursor: "pointer",
      transition: "0.3s",
    },
    stepLine: {
      background: "linear-gradient(135deg, #fff7e6, #fff4f9)",
      border: "2px dashed #ff9a9e",
      padding: "12px 16px",
      marginBottom: "12px",
      borderRadius: "16px",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      flexWrap: "wrap",
    },
    feedbackOk: { color: "#27ae60", fontWeight: "700", marginTop: "6px" },
    feedbackBad: { color: "#e74c3c", fontWeight: "700", marginTop: "6px" },
    resultDecimal: {
      fontFamily: "monospace",
      fontSize: "22px",
      padding: "8px 14px",
      borderRadius: "12px",
      border: "2px solid #ff4c4c",
      background: "#fff0f5",
      display: "inline-block",
      marginTop: "8px",
      fontWeight: "700",
    },
  };

  return (
    <div style={styles.body}>
      <div style={styles.card}>
        <h1 style={styles.title}>Binary → Decimal </h1>
        <div style={styles.meta}>
          Convert a binary number to decimal by calculating each bit’s value and sum. 
        </div>

        <div style={styles.row}>
          <label style={styles.label}>Binary:</label>
          <input
            type="text"
            placeholder="e.g. 1101"
            value={binary}
            onChange={(e) => setBinary(e.target.value)}
            style={styles.input}
          />
          <button onClick={handleStart} style={styles.button}>
            Start 
          </button>
          <button onClick={handleReset} style={styles.secondary}>
            Reset 
          </button>
        </div>

        {showActivity && (
          <>
            {stepIndex < bits.length && (
              <div style={styles.stepLine}>
                <div>
                  Step {stepIndex + 1}: bit = {bits[stepIndex]}, position = {stepIndex} 
                </div>
                <div style={{ display: "flex", gap: "8px" }}>
                  <input
                    type="number"
                    placeholder="bit × 2^pos"
                    value={valueInput}
                    onChange={(e) => setValueInput(e.target.value)}
                    style={{ ...styles.input, width: "120px" }}
                  />
                  <button onClick={handleSubmitStep} style={styles.button}>
                    Submit 
                  </button>
                </div>
              </div>
            )}

            {feedback && <div style={styles.feedbackBad}>{feedback}</div>}

            {calculatedValues.length > 0 && (
              <div style={{ marginTop: "20px" }}>
                {calculatedValues.map((val, idx) => (
                  <div key={idx} style={styles.stepLine}>
                    <div>
                      <strong>{bits[idx]} × 2^{idx}</strong> 
                    </div>
                    <div>= {val}</div>
                  </div>
                ))}
              </div>
            )}

            {showFinal && (
              <div style={{ marginTop: "24px", textAlign: "center" }}>
                <p>Now sum all the values to find the decimal:</p>
                <input
                  type="number"
                  placeholder="e.g. 13"
                  value={finalAnswer}
                  onChange={(e) => setFinalAnswer(e.target.value)}
                  style={styles.input}
                />
                <div
                  style={{
                    marginTop: "14px",
                    display: "flex",
                    justifyContent: "center",
                    gap: "12px",
                  }}
                >
                  <button onClick={handleCheckFinal} style={styles.button}>
                    Check ✅
                  </button>
                  <button onClick={handleShowSolution} style={styles.secondary}>
                    Show Solution
                  </button>
                </div>

                {finalFeedback && (
                  <p
                    style={
                      finalFeedback.includes("✅")
                        ? styles.feedbackOk
                        : styles.feedbackBad
                    }
                  >
                    {finalFeedback}
                  </p>
                )}

                {showSolution && (
                  <div style={{ marginTop: "12px" }}>
                    <div style={{ fontSize: "13px", color: "#666" }}>Correct decimal:</div>
                    <div style={styles.resultDecimal}>{correctSum}</div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default BinaryToDecimalStep;
